import { FC, useEffect, useState } from 'react';
import { Button } from 'common-components';
import { styled } from '@mui/material/styles';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { stepLabelClasses } from '@mui/material/StepLabel';
import { useTranslationContext } from '../../contexts/translationContext';
import { StepLabel, Step, Stepper as MuiStepper, Box, CheckIcon } from '../../uiLibraryImports';
import { StepperProps, Steps } from './stepperProps';
import styles from './stepper.module.scss';

const QontoConnector = styled(StepConnector)(() => ({
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.lineVertical}`]: {
            borderColor: '#e2d8f7',
            borderImage: 'rgba(186, 167, 216, 1)',
            backdropFilter: 'blur(11px)',
            borderWidth: '0.4375rem'
        }
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.lineVertical}`]: {
            borderColor: '#e2d8f7',
            borderImage: 'rgba(186, 167, 216, 1)',
            backdropFilter: 'blur(11px)',
            borderWidth: '0.4375rem'
        }
    },
    [`& .${stepConnectorClasses.lineVertical}`]: {
        borderColor: '#baa7d8',
        borderImage: 'rgba(186, 167, 216, 1)',
        backdropFilter: 'blur(11px)',
        borderWidth: '0.4375rem'
    }
}));

const ColorlibStepLabel = styled(StepLabel)(() => ({
    [`& .${stepLabelClasses.label}`]: {
        [`&.${stepLabelClasses.completed}`]: {
            color: 'rgba(98, 98, 98, 0.5)',
            fontSize: '1rem',
            fontWeight: 700,
            marginLeft: '1.5rem'
        },
        [`&.${stepLabelClasses.active}`]: {
            color: '#626262',
            fontSize: '1rem',
            fontWeight: 700,
            marginLeft: '1.5rem'
        },
        color: 'rgba(98, 98, 98, 0.5)',
        fontSize: '1rem',
        fontWeight: 700,
        marginLeft: '1rem'
    }
}));

const Stepper: FC<StepperProps> = (props: StepperProps) => {
    const { steps, activeStep, setActiveStep, resetStepper } = props;
    const { translate } = useTranslationContext();
    const [lastVisitedIndex, setLastVisitedIndex] = useState<number>(0);
    const [currentActiveStep, setCurrentActiveStep] = useState<number>(activeStep);

    const stepsRef = steps.map(step => step.ref);

    useEffect(() => {
        if (activeStep >= lastVisitedIndex) {
            setLastVisitedIndex(activeStep);
        }
        if (resetStepper) {
            setLastVisitedIndex(activeStep);
        }
    }, [activeStep, resetStepper]);

    const handleNext = (step: Steps) => {
        step?.onClickNext?.();
        setActiveStep(prevActiveStep => {
            const newActiveStep = prevActiveStep + 1;
            return newActiveStep;
        });
    };

    useEffect(() => {
        if (activeStep != currentActiveStep) {
            const nextStepRef = steps[activeStep];
            if (nextStepRef && nextStepRef.ref && nextStepRef.ref.current) {
                nextStepRef.ref.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
                setCurrentActiveStep(activeStep);
            }
        }
    }, [activeStep, stepsRef]);

    const handleBack = () => {
        setActiveStep(prevStep => prevStep - 1);
    };

    const ColorlibStepIcon = (index: number, step: Steps) => {
        if (!step.isCompleted) {
            return <div className={styles.number}>{index + 1}</div>;
        }
        return (
            <div className={styles.test123}>
                <CheckIcon fontSize="small" />
            </div>
        );
    };

    return (
        <Box sx={{ marginLeft: '2rem' }}>
            <MuiStepper activeStep={lastVisitedIndex} orientation="vertical" connector={<QontoConnector />}>
                {steps.map((step, index) => {
                    return (
                        <Step key={step.label} style={{ zIndex: steps.length - index }}>
                            <ColorlibStepLabel
                                className={step.isCompleted ? styles.completed : styles.line}
                                optional={
                                    index < activeStep ? (
                                        <div className={styles['past-content']}> {step.content}</div>
                                    ) : null
                                }
                                StepIconComponent={() => ColorlibStepIcon(index, step)}
                            >
                                {step.label}
                            </ColorlibStepLabel>

                            {index <= lastVisitedIndex && index >= activeStep && (
                                <div
                                    ref={step.ref}
                                    className={
                                        step.isCompleted ? styles['content-completed'] : styles['content-wrapper']
                                    }
                                    style={step.style}
                                >
                                    <div className={styles.content}>
                                        {step.content}
                                        <div className={styles.description}>{step.description}</div>
                                    </div>
                                    <div className={styles.buttons}>
                                        {index > 0 && activeStep >= index && (
                                            <div
                                                className={
                                                    index === steps.length - 1 ? styles['back-space'] : styles.back
                                                }
                                            >
                                                <Button
                                                    onClick={() => handleBack()}
                                                    type="learningPanelAdminNoBg"
                                                    lowerCase={true}
                                                    text={translate('PreviousStep')}
                                                />
                                            </div>
                                        )}

                                        {index !== steps.length - 1 && (
                                            <Button
                                                onClick={
                                                    step.isNextEnabled && activeStep >= index
                                                        ? () => handleNext(step)
                                                        : undefined
                                                }
                                                type={
                                                    step.isNextEnabled && activeStep >= index
                                                        ? 'learningPanelAdmin'
                                                        : 'learningPanelAdminDisabled'
                                                }
                                                lowerCase={true}
                                                text={translate('NextStep')}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </Step>
                    );
                })}
            </MuiStepper>
        </Box>
    );
};

export default Stepper;
